"use client"
// PC 首页「客户证言」轮播：Embla 实现（文案为脱敏示例，经 home.ts testimonials 切片注入）
import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { BlurFade } from './BlurFade'
import { cn } from '@/lib/utils'
import { homeDict, type HomeTestimonialsDict } from '@/lib/i18n/dicts/home'

export interface TestimonialsSectionProps {
    dict?: HomeTestimonialsDict
    className?: string
}

export default function TestimonialsSection({ dict = homeDict.zh.testimonials, className }: TestimonialsSectionProps) {
    const [carouselRef, api] = useEmblaCarousel({ loop: true, align: 'start' })
    const [selected, setSelected] = useState(0)
    const [snapCount, setSnapCount] = useState(0)

    const onSelect = useCallback(() => {
        if (!api) return
        setSelected(api.selectedScrollSnap())
    }, [api])

    useEffect(() => {
        if (!api) return
        setSnapCount(api.scrollSnapList().length)
        onSelect()
        api.on('select', onSelect)
        api.on('reInit', onSelect)
        return () => {
            api.off('select', onSelect)
        }
    }, [api, onSelect])

    return (
        <section className={cn('w-full bg-white overflow-hidden', className)}>
            <div className="max-w-7xl mx-auto py-16 md:py-20 px-4 sm:px-6 lg:px-8">
                <BlurFade delay={0.05} inView>
                    <div className="text-center">
                        <p className="text-sm font-semibold tracking-widest text-indigo-600 uppercase">
                            {dict.kicker}
                        </p>
                        <h2 className="mt-2 text-2xl md:text-4xl font-black text-slate-900">
                            {dict.heading}
                        </h2>
                    </div>
                </BlurFade>

                <BlurFade delay={0.15} inView>
                    <div className="relative mt-12">
                        <div className="overflow-hidden" ref={carouselRef}>
                            <div className="flex">
                                {dict.items.map((item, i) => (
                                    <div key={i} className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 px-3">
                                        <figure className="flex h-full flex-col rounded-2xl bg-slate-50 p-8 ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:ring-indigo-300/60 hover:shadow-xl hover:shadow-indigo-500/10">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8 text-indigo-400/70">
                                                <path d="M7.17 6C4.9 7.6 3.5 10.02 3.5 13.14c0 2.9 1.9 4.86 4.36 4.86 2.18 0 3.86-1.66 3.86-3.8 0-2.06-1.56-3.62-3.6-3.62-.36 0-.78.06-.94.1.3-1.58 1.9-3.58 3.6-4.54L7.17 6Zm9.9 0c-2.28 1.6-3.68 4.02-3.68 7.14 0 2.9 1.9 4.86 4.36 4.86 2.18 0 3.86-1.66 3.86-3.8 0-2.06-1.56-3.62-3.6-3.62-.36 0-.78.06-.94.1.3-1.58 1.9-3.58 3.6-4.54L17.07 6Z" />
                                            </svg>
                                            <blockquote className="mt-5 flex-1 text-sm md:text-base leading-7 text-slate-600">
                                                {item.quote}
                                            </blockquote>
                                            <figcaption className="mt-6 border-t border-black/5 pt-4">
                                                <p className="text-sm font-bold text-slate-900">{item.company}</p>
                                                <p className="mt-0.5 text-xs text-slate-500">{item.role}</p>
                                            </figcaption>
                                        </figure>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 上一张 / 下一张 */}
                        <button
                            type="button"
                            aria-label="Previous testimonial"
                            onClick={() => api?.scrollPrev()}
                            className="absolute -left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-500 ring-1 ring-black/10 shadow-sm transition-colors duration-300 hover:text-indigo-600 hover:ring-indigo-300"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            aria-label="Next testimonial"
                            onClick={() => api?.scrollNext()}
                            className="absolute -right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-500 ring-1 ring-black/10 shadow-sm transition-colors duration-300 hover:text-indigo-600 hover:ring-indigo-300"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                            </svg>
                        </button>
                    </div>
                </BlurFade>

                {/* 圆点指示器 */}
                <div className="mt-8 flex justify-center gap-2">
                    {Array.from({ length: snapCount }).map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Go to testimonial ${i + 1}`}
                            onClick={() => api?.scrollTo(i)}
                            className={cn(
                                'h-1.5 rounded-full transition-all duration-300',
                                selected === i ? 'w-8 bg-gradient-to-r from-indigo-500 to-fuchsia-500' : 'w-3 bg-slate-300 hover:bg-slate-400'
                            )}
                        />
                    ))}
                </div>
            </div>
        </section>
    )
}
